/**
 * @fileoverview Hydraulic Balancing Testing — next-generation educational
 * simulation for hydraulic balancing of heat-pump heating systems.
 *
 * Key concepts vs the original HydraulicBalancing simulation:
 *  - COUPLED hydraulic network: a pump curve, shared risers and nine parallel
 *    branches solved exactly — touching one valve shifts ALL flows.
 *  - 4C/ID instructional design:
 *      1. Learning tasks      → MissionPanel (5 whole tasks, fading support)
 *      2. Supportive info     → TheoryDrawer (domain theory + diagrams)
 *      3. Procedural info     → JIT popovers on every control
 *      4. Part-task practice  → DrillsPanel (calculator + flashcards)
 *  - Professional commissioning workflow (Verfahren B): heat load → design
 *    flow → preset → lower the heating curve & pump head.
 *
 * @module HydraulicBalancingTesting
 * @version 1.0.0
 */

import React, { useState, useCallback } from 'react';
import { Button, Segmented, Tabs, Tooltip, Space, Badge } from 'antd';
import {
  CaretRightOutlined,
  PauseOutlined,
  ReloadOutlined,
  BookOutlined,
  PartitionOutlined,
  FireOutlined,
  RollbackOutlined,
  ExperimentOutlined,
  FlagOutlined,
  ControlOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  AimOutlined,
} from '@ant-design/icons';

import PagesWrapper from '../../../PagesWrapper';

import {
  BuildingView,
  KPIDashboard,
  SystemControls,
  RoomControls,
  MissionPanel,
  TheoryDrawer,
  ScoresDrawer,
  DrillsPanel,
  AnalysisPanel,
  OptimizationPanel,
  PipeEditorDrawer,
  PipeTheoryDrawer,
  OptimizationTheoryDrawer,
} from './components';

import { useSimulationEngine, useMissionEngine, useOverlay, OVERLAYS } from './hooks';
import { SIM, RADIATOR, PRESET_POSITIONS } from './constants';

import styles from './HydraulicBalancingTesting.module.css';

const OVERLAY_ICONS = {
  deviation: <PartitionOutlined />,
  temperature: <FireOutlined />,
  return: <RollbackOutlined />,
  heatflux: <ThunderboltOutlined />,
};

export function HydraulicBalancingTestingSimulation() {
  const engine = useSimulationEngine();
  const { overlay, setOverlay, toggleOverlay } = useOverlay();
  const missionEngine = useMissionEngine(engine, { onOverlayChange: setOverlay });

  const [selectedRoom, setSelectedRoom] = useState(0);
  const [activeTab, setActiveTab] = useState('mission');
  const [theoryOpen, setTheoryOpen] = useState(false);
  const [theoryTopic, setTheoryTopic] = useState(null);
  const [scoresInfoOpen, setScoresInfoOpen] = useState(false);
  const [pipeEditorOpen, setPipeEditorOpen] = useState(false);
  const [pipeTheoryOpen, setPipeTheoryOpen] = useState(false);
  const [optTheoryOpen, setOptTheoryOpen] = useState(false);

  const openTheory = useCallback((topicKey) => {
    setTheoryTopic(topicKey || null);
    setTheoryOpen(true);
  }, []);

  const handleSelectRoom = useCallback((idx) => {
    setSelectedRoom(idx);
    setActiveTab('controls'); // always reveal the clicked room's values
  }, []);

  const sandbox = !missionEngine.activeMission;

  // Direct manipulation on the SVG: clicking a radiator / prevalve cycles to the
  // next value (wraps), respecting the same mission/sandbox locks as the panel.
  const { setRoomProp, setRoomPreset } = engine;
  const { isPresetEditable } = missionEngine;

  const cycleRadiator = useCallback((idx) => {
    if (!sandbox) return;
    setSelectedRoom(idx);
    const room = engine.rooms[idx];
    const sizes = RADIATOR.SIZES;
    const cur = sizes.indexOf(room.radiatorNominal);
    const next = sizes[(cur + 1) % sizes.length];
    setRoomProp(idx, 'radiatorNominal', next);
  }, [sandbox, engine.rooms, setRoomProp]);

  const cyclePreset = useCallback((idx) => {
    if (!isPresetEditable(idx)) return;
    setSelectedRoom(idx);
    const room = engine.rooms[idx];
    const next = (room.presetIndex + 1) % PRESET_POSITIONS.length;
    setRoomPreset(idx, next);
  }, [isPresetEditable, engine.rooms, setRoomPreset]);

  const tabs = [
    {
      key: 'mission',
      label: (
        <span>
          <FlagOutlined /> Missions
          {missionEngine.evaluation?.passed && <Badge status="success" style={{ marginLeft: 4 }} />}
        </span>
      ),
      children: <MissionPanel missionEngine={missionEngine} onOpenTheory={openTheory} />,
    },
    {
      key: 'controls',
      label: <span><ControlOutlined /> Controls</span>,
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <SystemControls
            engine={engine}
            isSystemEditable={missionEngine.isSystemEditable}
            onOpenPipeEditor={() => setPipeEditorOpen(true)}
            onOpenPipeTheory={() => setPipeTheoryOpen(true)}
          />
          <RoomControls
            engine={engine}
            roomIdx={selectedRoom}
            showTargets={missionEngine.showTargets}
            presetEditable={missionEngine.isPresetEditable(selectedRoom)}
            sandbox={sandbox}
          />
        </Space>
      ),
    },
    {
      key: 'analysis',
      label: <span><LineChartOutlined /> Analysis</span>,
      children: <AnalysisPanel engine={engine} showTargets={missionEngine.showTargets} />,
    },
    {
      key: 'optimization',
      label: <span><AimOutlined /> Optimization</span>,
      children: (
        <OptimizationPanel
          engine={engine}
          sandbox={sandbox}
          onOpenTheory={() => setOptTheoryOpen(true)}
        />
      ),
    },
    {
      key: 'drills',
      label: <span><ExperimentOutlined /> Drills</span>,
      children: <DrillsPanel />,
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.simulationContainer}>
        {/* header */}
        <p className={styles.title}>Hydraulic Balancing — Training Edition</p>
        <p className={styles.subtitle}>
          Nine rooms, one pump, one network: learn Verfahren B hydraulic balancing for heat pumps.
        </p>

        {/* toolbar */}
        <div className={styles.toolbar}>
          <Button
            type="primary"
            icon={engine.isRunning ? <PauseOutlined /> : <CaretRightOutlined />}
            onClick={() => engine.setIsRunning(!engine.isRunning)}
          >
            {engine.isRunning ? 'Pause' : 'Run'}
          </Button>
          <Segmented
            value={engine.speed}
            onChange={engine.setSpeed}
            options={SIM.SPEEDS.map((s) => ({ value: s, label: `${s}×` }))}
          />
          <Tooltip title={sandbox ? 'Reset the sandbox scenario' : 'Restart the current mission'}>
            <Button
              icon={<ReloadOutlined />}
              onClick={sandbox ? () => engine.loadScenario({ running: false }) : missionEngine.restartMission}
            >
              Reset
            </Button>
          </Tooltip>

          <span className={styles.toolbarDivider} />

          <span className={styles.toolbarLabel}>Overlays:</span>
          {OVERLAYS.map((o) => (
            <Tooltip key={o.key} title={o.label}>
              <Button
                type={overlay === o.key ? 'primary' : 'default'}
                ghost={overlay === o.key}
                icon={OVERLAY_ICONS[o.key]}
                onClick={() => toggleOverlay(o.key)}
              />
            </Tooltip>
          ))}

          <span className={styles.toolbarDivider} />

          <Button icon={<BookOutlined />} onClick={() => openTheory(null)}>
            Theory
          </Button>
        </div>

        {/* KPI strip */}
        <KPIDashboard engine={engine} onOpenScoresInfo={() => setScoresInfoOpen(true)} />

        {/* main content */}
        <div className={styles.mainContent}>
          <div className={styles.svgContainer}>
            <BuildingView
              rooms={engine.rooms}
              derived={engine.derived}
              params={engine.params}
              isRunning={engine.isRunning}
              overlay={overlay}
              selectedRoom={selectedRoom}
              onSelectRoom={handleSelectRoom}
              showTargets={missionEngine.showTargets}
              isPresetEditable={missionEngine.isPresetEditable}
              sandbox={sandbox}
              onCycleRadiator={cycleRadiator}
              onCyclePreset={cyclePreset}
            />
          </div>

          <div className={styles.sidePanel}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} size="small" />
          </div>
        </div>

        <TheoryDrawer open={theoryOpen} topicKey={theoryTopic} onClose={() => setTheoryOpen(false)} />
        <ScoresDrawer open={scoresInfoOpen} onClose={() => setScoresInfoOpen(false)} />
        <PipeEditorDrawer
          open={pipeEditorOpen}
          onClose={() => setPipeEditorOpen(false)}
          engine={engine}
          locked={!missionEngine.isSystemEditable('pipes')}
          onOpenTheory={() => setPipeTheoryOpen(true)}
        />
        <PipeTheoryDrawer open={pipeTheoryOpen} onClose={() => setPipeTheoryOpen(false)} />
        <OptimizationTheoryDrawer open={optTheoryOpen} onClose={() => setOptTheoryOpen(false)} />
      </div>
    </PagesWrapper>
  );
}

export default HydraulicBalancingTestingSimulation;
